import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "hc:inline-flex hc:items-center hc:justify-center hc:min-w-fit hc:gap-2 hc:whitespace-nowrap hc:rounded-sm hc:text-sm hc:font-medium hc:transition-all hc:disabled:pointer-events-none hc:disabled:opacity-50 hc:[&_svg]:pointer-events-none hc:[&_svg:not([class*='size-'])]:size-4 hc:shrink-0 hc:[&_svg]:shrink-0 hc:outline-none hc:focus-visible:border-ring hc:focus-visible:ring-ring/50 hc:focus-visible:ring-[3px] hc:aria-invalid:ring-destructive/20 hc:dark:aria-invalid:ring-destructive/40 hc:aria-invalid:border-destructive hc:cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'hc:bg-buttons-primary hc:text-primary-foreground hc:shadow-xs hc:hover:bg-buttons-primary/90 hc:transition-colors',
        destructive:
          'hc:bg-destructive hc:text-white hc:shadow-xs hc:hover:bg-destructive/85 hc:focus-visible:ring-destructive/20 hc:dark:focus-visible:ring-destructive/40 hc:dark:bg-destructive/60 hc:transition-colors',
        'destructive-outline':
          'hc:bg-background-error-soft hc:text-destructive hc:shadow-xs hc:hover:bg-destructive/20 hc:focus-visible:ring-destructive/20 hc:dark:focus-visible:ring-destructive/40 hc:dark:bg-destructive/60 hc:transition-colors',
        outline:
          'hc:border hc:bg-background hc:shadow-xs hc:hover:bg-accent hc:hover:text-accent-foreground hc:dark:bg-input/30 hc:dark:border-input hc:dark:hover:bg-input/50',
        secondary:
          'hc:bg-secondary hc:text-secondary-foreground hc:shadow-xs hc:hover:bg-secondary/80 hc:transition-colors',
        ghost: 'hc:hover:bg-accent hc:hover:text-accent-foreground hc:dark:hover:bg-accent/50',
        link: 'hc:text-primary hc:underline-offset-4 hc:hover:underline',
        primaryAlt:
          'hc:bg-buttons-primary-alt hc:text-white hc:shadow-xs hc:hover:bg-buttons-primary-alt/90 hc:transition-colors',
      },
      size: {
        default: 'hc:h-9 hc:px-4 hc:py-2 hc:has-[>svg]:px-3',
        sm: 'hc:h-8 hc:rounded-md hc:gap-1.5 hc:px-3 hc:has-[>svg]:px-2.5',
        lg: 'hc:h-10 hc:rounded-md hc:px-6 hc:has-[>svg]:px-4',
        icon: 'hc:size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot='button'
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button };
